import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { AssignmentList } from 'modules/finance/components/CostCenterAssigments/Assigments/AssignmentList'
import { CostCenterAssignmentBaseCard } from 'modules/finance/components/CostCenterAssigments/Assigments/BaseCard'
import { DeleteCostCenterAssignmentModal } from 'modules/finance/components/CostCenterAssigments/Assigments/DeleteModal'
import { ManageAssignments } from 'modules/finance/components/CostCenterAssigments/Assigments/ManageAssignments'
import { CostCenterAssignment } from 'modules/finance/lib/costCenterAssignments'

interface Props {
  allAssignments: CostCenterAssignment[]
  assignments: CostCenterAssignment[]
  legalEntityIds: number[]
  resourceId: number
  openOnRender?: boolean
  canManageCostCenterMemberships: boolean
  onSave?: () => void
  onDelete?: () => void
  onToggleEdit: (value: boolean) => void
}

export const CostCenterAssignmentEditableCard = ({
  allAssignments,
  assignments,
  legalEntityIds,
  resourceId,
  openOnRender,
  canManageCostCenterMemberships,
  onSave,
  onDelete,
  onToggleEdit,
}: Props) => {
  const [editable, setEditable] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)

  const handleOnSave = () => {
    onSave?.()

    setEditable(false)
  }

  const handleDelete = () => {
    setEditable(false)
    setDeleteModal(false)

    onDelete?.()
  }

  const options = {
    onEdit: () => setEditable(true),
    onDelete: () => setDeleteModal(true),
  }

  const ids = assignments.map((assignment) => assignment.id)

  useEffect(() => {
    if (openOnRender) {
      // This is opening the assignment to allow the edit
      setEditable(true)
    }
  }, [openOnRender])

  useEffect(() => {
    // This is a callback to the parent component, to be able to manage the parent
    // when a box is being edited (for example, hiding the button for create a new assignment)
    onToggleEdit(editable)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable])

  return (
    <>
      <CostCenterAssignmentBaseCard focus={editable}>
        <Box paddingX="s24" paddingY="s20">
          <AssignmentList
            assignments={assignments}
            {...(!editable && canManageCostCenterMemberships && { options })}
          />
        </Box>

        {editable && (
          <>
            <Box
              width="full"
              height="s1"
              border={{ top: { style: 'solid', width: 's1', color: 'grey400' } }}
            />

            <ManageAssignments
              allAssignments={allAssignments}
              assignments={assignments}
              legalEntityIds={legalEntityIds}
              title={i18n.t('finance.cost_centers.assignments.editable_card.title')}
              description={i18n.t('finance.cost_centers.assignments.editable_card.description')}
              resourceId={resourceId}
              onCancel={() => setEditable(false)}
              onDelete={handleDelete}
              onSave={handleOnSave}
            />
          </>
        )}
      </CostCenterAssignmentBaseCard>

      {deleteModal && (
        <DeleteCostCenterAssignmentModal
          ids={ids}
          onClose={() => {
            setDeleteModal(false)
          }}
          onSubmit={handleDelete}
        />
      )}
    </>
  )
}
